import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/wallet/LegalPage";

export const Route = createFileRoute("/privacy")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Privacy Policy — Cobalt Wallet" },
      {
        name: "description",
        content:
          "How Cobalt Wallet handles data: keys and recovery phrases never leave your device, and no accounts or profiles are created.",
      },
      { property: "og:title", content: "Privacy Policy — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Cobalt Wallet is non-custodial: no accounts, no key escrow, no profile building.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://cobaltwalletxrpl.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Privacy"
      title="Privacy Policy"
      updated="30 August 2026"
      intro="Cobalt Wallet is a self-custody XRP Ledger wallet. We do not operate accounts, and we cannot see your keys, your recovery phrase or your passcode."
    >
      <LegalSection heading="What stays on your device">
        <p>
          Your recovery phrase, derived keys, optional 25th word and encrypted vault are created and
          stored only in your browser's local storage on this device. They are encrypted with
          AES-256-GCM using a key derived from your passcode or your device passkey.
        </p>
        <p>
          We have no server that receives, backs up or escrows this material. If you clear site data
          or lose the device without your recovery phrase, no one — including us — can restore it.
        </p>
      </LegalSection>

      <LegalSection heading="What leaves your device">
        <p>
          To show balances and submit transactions, the app connects directly to public XRP Ledger
          nodes. Those nodes receive your XRPL address and signed transactions, as with any wallet.
        </p>
        <p>
          Market, ETF and token data is fetched from public third-party providers. Those providers
          may log the IP address of your request under their own policies. No wallet address is sent
          to them.
        </p>
        <p>Signing always happens locally. Private keys are never transmitted.</p>
      </LegalSection>

      <LegalSection heading="What we do not do">
        <p>
          No accounts, no email collection, no advertising identifiers, no selling of data, and no
          profile building. We do not add analytics that link activity to your XRPL address.
        </p>
      </LegalSection>

      <LegalSection heading="Public ledger transparency">
        <p>
          The XRP Ledger is a public blockchain. Addresses, balances, trustlines and transactions —
          including the service fee payment disclosed in the app — are permanently visible to anyone.
          Privacy on-chain is your responsibility.
        </p>
      </LegalSection>

      <LegalSection heading="Your controls">
        <p>
          You can remove all local data at any time by deleting the wallet in Settings or clearing
          site data in your browser. This is irreversible without your recovery phrase.
        </p>
      </LegalSection>

      <LegalSection heading="Changes and contact">
        <p>
          Material changes to this policy will be reflected on this page with a new update date. For
          privacy questions, contact the operator of this deployment.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
